import { useState, useEffect } from "react";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function RewardSettingsCard() {
  const swapDate = (v) => { const p = String(v || "").split("-"); return p.length === 2 ? `${p[1]}-${p[0]}` : v; };
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newDate, setNewDate] = useState("");
  useEffect(() => {
    api.get("/reward/settings").then((r) => setS({
      reward_period_start: r.data.reward_period_start || "04-01",
      reward_period_end: r.data.reward_period_end || "03-31",
      christmas_shutdown_dates: r.data.christmas_shutdown_dates || [],
      birthday_leave_enabled: r.data.birthday_leave_enabled ?? true,
      birthday_alt_day_enabled: r.data.birthday_alt_day_enabled ?? false,
      auto_carry_publication: r.data.auto_carry_publication ?? true,
      pension_qe_lower: r.data.pension_qe_lower ?? 6240,
    })).catch(() => toast.error("Could not load reward settings"));
  }, []);
  if (!s) return <div className="text-slate-400 text-sm" data-testid="reward-settings-loading">Loading reward settings…</div>;
  const addDate = () => { if (newDate && !s.christmas_shutdown_dates.includes(newDate)) { setS({ ...s, christmas_shutdown_dates: [...s.christmas_shutdown_dates, newDate].sort() }); setNewDate(""); } };
  const removeDate = (d) => setS({ ...s, christmas_shutdown_dates: s.christmas_shutdown_dates.filter((x) => x !== d) });
  const save = async () => { setSaving(true); try { await api.put("/reward/settings", s); toast.success("Reward settings saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); } };
  const rows = [
    ["birthday_leave_enabled", "Birthday leave", "Include a paid day off on the employee's birthday (when it is a working day) as additional leave."],
    ["birthday_alt_day_enabled", "Alternative day when birthday is non-working", "If the birthday falls on a non-working day, allow an alternative day."],
    ["auto_carry_publication", "Auto-carry published statements", "Keep a published statement visible into the next reward year automatically (values still recalculate live), without re-publishing."],
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5 max-w-2xl" data-testid="reward-settings-card">
      <div>
        <h3 className="font-semibold text-[#0B1A2F]">Reward period</h3>
        <p className="text-xs text-slate-400 mb-2">The 12-month period a Total Reward statement covers. Use DD-MM (e.g. 01-04 for 1 April).</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-500">Period start (DD-MM)</Label><Input value={swapDate(s.reward_period_start)} onChange={(e) => setS({ ...s, reward_period_start: swapDate(e.target.value) })} placeholder="01-04" data-testid="reward-period-start" /></div>
          <div><Label className="text-xs text-slate-500">Period end (DD-MM)</Label><Input value={swapDate(s.reward_period_end)} onChange={(e) => setS({ ...s, reward_period_end: swapDate(e.target.value) })} placeholder="31-03" data-testid="reward-period-end" /></div>
        </div>
      </div>
      <div className="border-t border-slate-100 pt-4">
        <h3 className="font-semibold text-[#0B1A2F]">Christmas shutdown dates</h3>
        <p className="text-xs text-slate-400 mb-2">Paid days over the Christmas shutdown, valued as additional leave. Dates falling on a bank holiday are automatically excluded.</p>
        <div className="flex items-end gap-2 mb-3">
          <div><Label className="text-xs text-slate-500">Add a date</Label><Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-48" data-testid="reward-christmas-input" /></div>
          <Button variant="outline" onClick={addDate} data-testid="reward-christmas-add"><Plus size={15} className="mr-1" />Add</Button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="reward-christmas-list">
          {s.christmas_shutdown_dates.length === 0 ? <p className="text-xs text-slate-400">No dates added.</p> :
            s.christmas_shutdown_dates.map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 bg-slate-100 rounded-full px-3 py-1 text-xs text-[#0B1A2F]" data-testid={`reward-christmas-${d}`}>{fmtDate(d)}<button onClick={() => removeDate(d)} className="text-slate-400 hover:text-rose-500" data-testid={`reward-christmas-remove-${d}`}><Trash2 size={12} /></button></span>
            ))}
        </div>
      </div>
      <div className="border-t border-slate-100 pt-4">
        <h3 className="font-semibold text-[#0B1A2F]">Pension qualifying earnings</h3>
        <p className="text-xs text-slate-400 mb-2">The lower earnings threshold deducted before applying the employer pension %. Employer pension = (basic salary − this figure) × the % on the pension benefit. Update it each tax year.</p>
        <div className="w-48">
          <Label className="text-xs text-slate-500">Lower threshold (£/yr)</Label>
          <Input type="number" value={s.pension_qe_lower} onChange={(e) => setS({ ...s, pension_qe_lower: e.target.value === "" ? "" : Number(e.target.value) })} placeholder="6240" data-testid="reward-pension-qe" />
        </div>
      </div>
      <div className="border-t border-slate-100 pt-2">
        <h3 className="font-semibold text-[#0B1A2F] mb-1">Rules</h3>
        {rows.map(([k, label, desc]) => (
          <div key={k} className="flex items-start justify-between gap-4 py-3 border-t border-slate-100 first:border-t-0">
            <div><p className="text-sm font-medium text-[#0B1A2F]">{label}</p><p className="text-xs text-slate-400">{desc}</p></div>
            <Switch checked={!!s[k]} onCheckedChange={(v) => setS({ ...s, [k]: v })} data-testid={`reward-${k.replace(/_/g, "-")}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="reward-settings-save"><Save size={15} className="mr-2" />{saving ? "Saving…" : "Save reward settings"}</Button>
    </div>
  );
}
